import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Download, Upload, Pencil, Trash, Search, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { questionsAPI, questionBankAPI } from "../../services/api";
import { ModernBadge, ModernLoading, ModernPaginationFooter } from "@/components/common/ModernPageTemplate";
import { format } from "date-fns";

const ViewQuestionsModal = ({ open, onOpenChange, questionBank }) => {
  const { toast } = useToast();
  
  // State management
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDifficulty, setSelectedDifficulty] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [isPaginationEnabled, setIsPaginationEnabled] = useState(true);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [bankDetails, setBankDetails] = useState(null);

  // Fetch questions when modal opens or filters change
  useEffect(() => {
    if (open && questionBank?._id) {
      fetchQuestions();
      fetchBankDetails();
    }
  }, [open, questionBank, currentPage, rowsPerPage, isPaginationEnabled, searchTerm, selectedDifficulty]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setSelectedDifficulty("all");
      setCurrentPage(1);
      setQuestions([]);
    }
  }, [open]);

  const fetchBankDetails = async () => {
    try {
      const response = await questionBankAPI.getOne(questionBank._id);
      setBankDetails(response.data);
    } catch (error) {
      console.error("Failed to fetch bank details:", error);
    }
  };

  const fetchQuestions = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: isPaginationEnabled ? currentPage : 1,
        limit: isPaginationEnabled ? rowsPerPage : 999999,
        questionBankId: questionBank._id,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedDifficulty !== "all" && { difficultyLevel: selectedDifficulty }),
      };

      const response = await questionsAPI.getAll(params);
      setQuestions(response.data.questions || []);
      setTotalQuestions(response.data.total || 0);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error("Failed to fetch questions:", error);
      toast({
        title: "Error",
        description: "Failed to load questions",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleDifficultyChange = (value) => {
    setSelectedDifficulty(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePaginationToggle = (enabled) => {
    setIsPaginationEnabled(enabled);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const getDifficultyBadge = (difficulty) => {
    const configs = {
      easy: { variant: "language", color: "text-green-700" },
      medium: { variant: "template", color: "text-yellow-700" },
      hard: { variant: "language", color: "text-red-700" },
    };
    const config = configs[difficulty?.toLowerCase()] || configs.medium;
    
    return (
      <ModernBadge variant={config.variant}>
        <span className={`text-xs ${config.color}`}>{difficulty || "medium"}</span>
      </ModernBadge>
    );
  };

  const getCapacityInfo = () => {
    if (!bankDetails) return null;
    
    const currentCount = bankDetails.questionCount || 0;
    const maxCapacity = bankDetails.maxCapacity || 5000;
    const percentage = Math.round((currentCount / maxCapacity) * 100);
    
    return {
      current: currentCount,
      max: maxCapacity,
      percentage,
    };
  };

  const capacityInfo = getCapacityInfo();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[98vw] h-[97vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="text-2xl font-bold">
                Questions - {questionBank?.name || "Question Bank"}
              </DialogTitle>
              <p className="text-sm text-gray-600 mt-1">
                {questionBank?.description || "Manage questions in this bank"}
              </p>
              {capacityInfo && (
                <div className="mt-2 flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    Capacity: {capacityInfo.current}/{capacityInfo.max} questions ({capacityInfo.percentage}% used)
                  </span>
                  <div className="flex-1 max-w-xs">
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${capacityInfo.percentage >= 90 ? 'bg-red-500' : capacityInfo.percentage >= 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                        style={{ width: `${capacityInfo.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Filters */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search questions..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10"
              />
            </div>
            <Select value={selectedDifficulty} onValueChange={handleDifficultyChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Difficulties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Difficulties</SelectItem>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Questions Table */}
        <div className="flex-1 overflow-auto px-6 pt-4 pb-12">
          {isLoading ? (
            <ModernLoading text="Loading questions..." />
          ) : questions.length > 0 ? (
            <div className="admin-table-container mb-8">
              <table className="admin-table">
                <thead className="admin-table-header">
                  <tr>
                    <th className="admin-table-cell" style={{ width: "60px" }}>S.No</th>
                    <th className="admin-table-cell" style={{ minWidth: "300px" }}>Question</th>
                    <th className="admin-table-cell" style={{ width: "150px" }}>Category</th>
                    <th className="admin-table-cell" style={{ width: "150px" }}>Type</th>
                    <th className="admin-table-cell" style={{ width: "120px" }}>Difficulty</th>
                    <th className="admin-table-cell" style={{ width: "80px" }}>Points</th>
                    <th className="admin-table-cell" style={{ width: "200px" }}>Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((question, index) => (
                    <tr key={question._id} className="admin-table-row">
                      <td className="admin-table-cell number">
                        {isPaginationEnabled ? (currentPage - 1) * rowsPerPage + index + 1 : index + 1}
                      </td>
                      <td className="admin-table-cell">
                        <div className="text-sm line-clamp-2" title={question.question}>
                          {question.question}
                        </div>
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-sm">{question.category || "N/A"}</span>
                      </td>
                      <td className="admin-table-cell">
                        <ModernBadge variant="template">
                          <span className="text-xs">{question.qtyp || "N/A"}</span>
                        </ModernBadge>
                      </td>
                      <td className="admin-table-cell">
                        {getDifficultyBadge(question.difficultyLevel)}
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-sm font-medium">{question.value || 1}</span>
                      </td>
                      <td className="admin-table-cell">
                        <div className="flex flex-wrap gap-1">
                          {question.tags && question.tags.length > 0 ? (
                            question.tags.slice(0, 3).map((tag, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">No tags</span>
                          )}
                          {question.tags && question.tags.length > 3 && (
                            <span className="text-xs text-gray-500">+{question.tags.length - 3}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">No questions found</p>
              <p className="text-sm mt-2">
                {searchTerm || selectedDifficulty !== "all"
                  ? "Try adjusting your filters"
                  : "This question bank is empty"}
              </p>
            </div>
          )}
        </div>

        {/* Pagination Footer */}
        <div className="border-t bg-white flex-shrink-0">
          <ModernPaginationFooter
            isPaginationEnabled={isPaginationEnabled}
            onPaginationToggle={handlePaginationToggle}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            pagination={{
              current: currentPage,
              pages: totalPages,
              total: totalQuestions,
            }}
            onPageChange={handlePageChange}
            rowsOptions={[15, 25, 50, 100]}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewQuestionsModal;
